import { type HotkeyOptions, useHotkey } from "@/domains/hotkeys/use-hotkey";
import { actions } from "@/actions";
import { appRoutes } from "@/app/router";
import { EventContext } from "@/domains/metrics/context";
import { useAppStore } from "@/store";
import { resolveHotkey } from "@/modules/keyboard-shortcuts/platform";
import { KEYBOARD_SHORTCUT_BINDINGS, ShortcutBinding } from "@/modules/keyboard-shortcuts/registry";
import { executeEscapeShortcut } from "@/modules/keyboard-shortcuts/escape";
import { clientEnvModule } from "@/modules/client-env";
import { electronModule } from "@/modules/electron";
import { resolveChatSelectionAttachListState } from "@/modules/keyboard-shortcuts/resolve-chat-selection-attach-list-state";
import {
  focusPaneForKeyboardInteraction,
  getMainPanelSplitViewContainerWidth,
  getRelativeTabShortcutTabs,
  getVisiblePaneTabs,
  isCleanableNote,
  isNotesListPath,
} from "@/modules/keyboard-shortcuts/helpers";
import { NotesViewPageStore } from "@/store/pages/NotesViewPageStore/NotesViewPageStore";
import { NotesListPageStore } from "@/store/pages/NotesListPageStore";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import { useCallback } from "react";

const useRelativePinnedItemHotkey = ({
  action,
  index,
  openRelativePinnedItem,
  options,
}: {
  action: keyof typeof KEYBOARD_SHORTCUT_BINDINGS;
  index: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
  openRelativePinnedItem: (relativeIndex: number) => void;
  options: HotkeyOptions;
}) => {
  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS[action],
    () => {
      openRelativePinnedItem(index);
    },
    options
  );
};

const useResolvedHotkey = (
  binding: ShortcutBinding,
  handler: (event: KeyboardEvent) => void | Promise<void>,
  options: HotkeyOptions
) => {
  const hotkey = resolveHotkey(binding);

  useHotkey(hotkey, event => handler(event), options);
};

export const useAppKeyboardShortcuts = () => {
  const { pageStore, store } = useAppStore();
  const quickSearchIsClosed = !pageStore.quickSearchModal.isOpen;
  const globalHotkeysActive = quickSearchIsClosed && !store.interface.globalHotkeysBlocked;
  const gatedOptions: HotkeyOptions = {
    enabled: globalHotkeysActive,
  };

  // we're too high up in the Provider tree to use useTabPageStore
  const activeTabId = store.navigation.tabs.activeTabId;
  const activeTab = store.navigation.tabs.tabsMap.get(activeTabId) ?? null;
  const mainPanelSplitViewContainerWidth = getMainPanelSplitViewContainerWidth();
  const visiblePaneTabs = getVisiblePaneTabs({
    activeTabId,
    panesMap: store.navigation.tabs.panesMap,
    tabsMap: store.navigation.tabs.tabsMap,
    getResponsivePanes: containerWidth => store.navigation.tabs.getResponsivePanes(containerWidth),
    containerWidth: mainPanelSplitViewContainerWidth,
  });
  const visibleSelectionAttachListStates = visiblePaneTabs
    .map(tab =>
      resolveChatSelectionAttachListState({
        pageStore,
        activeTab: tab,
        activeTabPath: tab.path,
      })
    )
    .filter((listState): listState is ListStateObservable => !!listState);
  const selectedSelectionAttachListStates = visibleSelectionAttachListStates.filter(
    listState => listState.canChatWithSelection
  );
  const selectedVisibleAddToCollectionListStates = visibleSelectionAttachListStates.filter(
    listState => listState.canAddToCollection && listState.selectedItemIds.length > 0
  );
  const selectedVisibleAddToCollectionNoteIds = Array.from(
    new Set(
      selectedVisibleAddToCollectionListStates.flatMap(listState => listState.selectedItemIds)
    )
  );
  const visibleNotesListPageStores = visiblePaneTabs
    .filter(tab => isNotesListPath(tab.path))
    .map(tab => pageStore.getPageStore(tab, tab.path, "notesListPage") as NotesListPageStore | null)
    .filter((notesListPageStore): notesListPageStore is NotesListPageStore => !!notesListPageStore);
  const selectedVisibleNotesListPageStores = visibleNotesListPageStores.filter(
    notesListPageStore => notesListPageStore.listState.isSelectModeActive
  );
  const selectedVisibleNoteIds = Array.from(
    new Set(
      selectedVisibleNotesListPageStores.flatMap(
        notesListPageStore => notesListPageStore.listState.selectedItemIds
      )
    )
  );
  const selectedVisibleNotes = selectedVisibleNoteIds
    .map(noteId => store.notes.getNoteObservableById({ noteId }))
    .filter(note => note != null);
  const tabNotePageStore = activeTab
    ? ((pageStore.getPageStore(
        activeTab,
        activeTab.path,
        "notesViewPage"
      ) as NotesViewPageStore | null) ?? undefined)
    : undefined;
  const tabNotesListPageStore = activeTab
    ? ((pageStore.getPageStore(
        activeTab,
        activeTab.path,
        "notesListPage"
      ) as NotesListPageStore | null) ?? undefined)
    : undefined;
  const hasNoteViewTabOpen = !!tabNotePageStore;
  const hasCrossPaneSelectedNotes = selectedVisibleNoteIds.length > 0;
  const activeNoteIsInVersionHistory =
    !!tabNotePageStore?.noteVersionHistory?.currentNoteId &&
    tabNotePageStore.noteVersionHistory.currentNoteId === tabNotePageStore.noteId;
  const highlightedNotesListItemId = tabNotesListPageStore?.listState.highlightedItemId;
  const notesListMultiSelectIsActive = tabNotesListPageStore?.listState.isSelectModeActive ?? false;
  const canPinHighlightedNotesListItem =
    !notesListMultiSelectIsActive &&
    !!highlightedNotesListItemId &&
    !!tabNotesListPageStore?.orderedItemIds.includes(highlightedNotesListItemId);
  const mayHaveCollectionViewTabOpen =
    !!activeTab?.path &&
    (activeTab.path.startsWith("/collections/") || activeTab.path.startsWith("/c/"));
  const findInNoteIsOpen = tabNotePageStore?.findInNote?.isOpen ?? false;
  const optionsEnabledOnNoteViewTab: HotkeyOptions = {
    enabled: globalHotkeysActive && hasNoteViewTabOpen,
  };
  const optionsEnabledOnNoteViewTabOutsideVersionHistory: HotkeyOptions = {
    enabled: globalHotkeysActive && hasNoteViewTabOpen && !activeNoteIsInVersionHistory,
  };
  const optionsEnabledForSelectedOrganizableNotesOrNoteView: HotkeyOptions = {
    enabled:
      globalHotkeysActive &&
      (selectedVisibleAddToCollectionNoteIds.length > 0 || hasNoteViewTabOpen),
  };
  const optionsEnabledForSelectedNotesOrNoteViewWithOverride: HotkeyOptions = {
    enabled: globalHotkeysActive && (hasCrossPaneSelectedNotes || hasNoteViewTabOpen),
    ignoreInputs: false,
    preventDefault: true,
  };
  const optionsEnabledWithFindInNoteOpen: HotkeyOptions = {
    enabled: globalHotkeysActive && findInNoteIsOpen,
  };
  const optionsEnabledOnNoteViewTabWithPreventDefault: HotkeyOptions = {
    enabled: globalHotkeysActive && hasNoteViewTabOpen,
    preventDefault: true,
  };
  const gatedOptionsWithOverride: HotkeyOptions = {
    enabled: globalHotkeysActive,
    ignoreInputs: false,
    preventDefault: true,
  };
  const canToggleSidebar = !store.layout.shouldHideSidebarToggle;
  const getActiveCollectionsViewPageStore = useCallback(() => {
    if (!activeTab) {
      return undefined;
    }

    return (
      (pageStore.getPageStore(activeTab, activeTab.path, "collectionsViewPage") as {
        handleCreateNewNote: (args?: { autoStartVoice?: boolean }) => Promise<void>;
        collectionObservable?: {
          toggleFavorite: () => Promise<void>;
        } | null;
      } | null) ?? undefined
    );
  }, [activeTab, pageStore]);
  const optionsEnabledForPinNote: HotkeyOptions = {
    enabled:
      quickSearchIsClosed &&
      (hasNoteViewTabOpen || mayHaveCollectionViewTabOpen || canPinHighlightedNotesListItem),
  };

  const toggleFocusMode = useCallback(() => {
    if (!canToggleSidebar) return;

    store.interface.setIsSidebarOpen(!store.interface.isSidebarOpen);
  }, [canToggleSidebar, store.interface]);

  const moveActiveNoteToTrashAndCloseTab = useCallback(async () => {
    if (!tabNotePageStore?.itemObservable) {
      return;
    }

    await tabNotePageStore.itemObservable.moveToTrash();
    store.navigation.tabs.closeTab({ tabId: store.navigation.tabs.activeTabId });
  }, [store.navigation.tabs, tabNotePageStore]);

  const openOrganizeForSelectedNotes = useCallback(() => {
    const primaryListState = selectedVisibleAddToCollectionListStates[0];
    if (!primaryListState || selectedVisibleAddToCollectionNoteIds.length === 0) {
      return false;
    }

    primaryListState.openAddToCollection({ noteIds: selectedVisibleAddToCollectionNoteIds });
    return true;
  }, [selectedVisibleAddToCollectionListStates, selectedVisibleAddToCollectionNoteIds]);

  const autoOrganizeSelectedNotes = useCallback(async () => {
    if (!store.floatingAgent.isMemAgentAccessEnabled || selectedVisibleNoteIds.length === 0) {
      return false;
    }

    const didAutoOrganize = await actions.autoOrganizeNotes({
      noteIds: selectedVisibleNoteIds,
      store,
    });

    if (didAutoOrganize) {
      selectedVisibleNotesListPageStores.forEach(notesListPageStore =>
        notesListPageStore.listState.clearSelection()
      );
    }

    return didAutoOrganize;
  }, [selectedVisibleNoteIds, selectedVisibleNotesListPageStores, store]);

  const chatWithCombinedSelection = useCallback(async () => {
    if (
      !store.floatingAgent.isMemAgentAccessEnabled ||
      selectedSelectionAttachListStates.length === 0
    ) {
      return false;
    }

    const noteIds = Array.from(
      new Set(selectedSelectionAttachListStates.flatMap(listState => listState.selectedChatNoteIds))
    );
    const collectionIds = Array.from(
      new Set(
        selectedSelectionAttachListStates.flatMap(listState => listState.selectedChatCollectionIds)
      )
    );

    if (noteIds.length === 0 && collectionIds.length === 0) {
      return false;
    }

    const didStartChat = await actions.startChatWithEntities({
      noteIds,
      collectionIds,
      store,
      overrideContext: false,
    });

    if (didStartChat) {
      selectedSelectionAttachListStates.forEach(listState => listState.clearSelection());
    }

    return didStartChat;
  }, [selectedSelectionAttachListStates, store]);

  const trashSelectedNotes = useCallback(async () => {
    if (!hasCrossPaneSelectedNotes || selectedVisibleNotes.length === 0) {
      return false;
    }

    selectedVisibleNotesListPageStores.forEach(notesListPageStore =>
      notesListPageStore.listState.clearSelection()
    );
    actions.moveNotesToTrash({
      notes: selectedVisibleNotes,
      store,
    });
    return true;
  }, [hasCrossPaneSelectedNotes, selectedVisibleNotes, selectedVisibleNotesListPageStores, store]);

  const goToRelativeTab = useCallback(
    (relativeIndex: number) => {
      const shortcutTabs = getRelativeTabShortcutTabs({
        activePaneId: store.navigation.tabs.activePaneId,
        containerWidth: mainPanelSplitViewContainerWidth,
        getResponsivePanes: containerWidth =>
          store.navigation.tabs.getResponsivePanes(containerWidth),
        getTabsForPane: paneId => store.navigation.tabs.getTabsForPane(paneId),
        panes: store.navigation.tabs.panes,
        panesMap: store.navigation.tabs.panesMap,
      });
      const targetTab = shortcutTabs[relativeIndex - 1];
      if (!targetTab) return;

      store.navigation.tabs.switchToTab({ tabId: targetTab.id });
    },
    [mainPanelSplitViewContainerWidth, store.navigation.tabs]
  );

  const closeMacDesktopWindowWhenOnlyOneTabRemains = useCallback(() => {
    if (!clientEnvModule.isDesktop() || !clientEnvModule.isMacOs()) {
      return false;
    }

    if (store.navigation.tabs.tabsMap.size !== 1) {
      return false;
    }

    void electronModule.closeWindow();
    return true;
  }, [store.navigation.tabs.tabsMap]);

  const openRelativePinnedItem = useCallback(
    (relativeIndex: number) => {
      const pinnedItem = store.favoriteItems.sortedFavoriteItems[relativeIndex - 1];
      if (!pinnedItem) return;

      switch (pinnedItem.itemKind) {
        case "COLLECTION":
          store.navigation.goToCollection({ collectionId: pinnedItem.itemId });
          return;
        case "NOTE":
          store.navigation.goToNote({ noteId: pinnedItem.itemId });
          return;
        default:
          return;
      }
    },
    [store.favoriteItems.sortedFavoriteItems, store.navigation]
  );

  const switchToNextPaneAndFocus = useCallback(() => {
    const orderedPanes =
      store.navigation.tabs.panes ??
      Array.from(store.navigation.tabs.panesMap.values()).sort((a, b) => a.order - b.order);
    if (orderedPanes.length <= 1) {
      store.navigation.tabs.createPane({});
      focusPaneForKeyboardInteraction(store.navigation.tabs.activePaneId);
      return;
    }

    const visiblePanes =
      typeof mainPanelSplitViewContainerWidth === "number" && mainPanelSplitViewContainerWidth > 0
        ? store.navigation.tabs.getResponsivePanes(mainPanelSplitViewContainerWidth).visiblePanes
        : orderedPanes;

    if (visiblePanes.length <= 1) {
      return;
    }

    const currentPaneIndex = visiblePanes.findIndex(
      pane => pane.id === store.navigation.tabs.activePaneId
    );
    const normalizedCurrentPaneIndex = currentPaneIndex >= 0 ? currentPaneIndex : 0;
    const nextPane = visiblePanes[(normalizedCurrentPaneIndex + 1) % visiblePanes.length];
    if (!nextPane?.activeTabId) {
      return;
    }

    store.navigation.tabs.switchToTab({
      tabId: nextPane.activeTabId,
    });
    focusPaneForKeyboardInteraction(nextPane.id);
  }, [mainPanelSplitViewContainerWidth, store.navigation.tabs]);

  const activeNoteId = tabNotePageStore?.noteId;

  // Global menu action Keyboard shortcuts
  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.create_note,
    () => {
      if (store.notes.newNoteIsBeingCreated) return;
      const activeCollectionsViewPageStore = getActiveCollectionsViewPageStore();
      if (activeCollectionsViewPageStore) {
        void activeCollectionsViewPageStore.handleCreateNewNote();
        return;
      }
      store.notes.composeNewNote({ eventContext: EventContext.KeyboardShortcut });
    },
    gatedOptions
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.create_note_voice,
    () => {
      if (store.notes.newNoteIsBeingCreated) return;
      const activeCollectionsViewPageStore = getActiveCollectionsViewPageStore();
      if (activeCollectionsViewPageStore) {
        void activeCollectionsViewPageStore.handleCreateNewNote({ autoStartVoice: true });
        return;
      }
      store.notes.composeNewNote({
        eventContext: EventContext.KeyboardShortcut,
        autoStartVoice: true,
      });
    },
    gatedOptions
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.search,
    () => {
      pageStore.quickSearchModal.toggleIsOpen();
    },
    {
      enabled: !store.interface.globalHotkeysBlocked,
    }
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.open_mem_agent,
    async () => {
      if (await chatWithCombinedSelection()) {
        return;
      }

      if (store.floatingAgent.isMemAgentAccessEnabled) {
        store.floatingAgent.requestOpenPanel();
      }
    },
    gatedOptions
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.show_heads_up,
    () => {
      if (store.floatingAgent.isHeadsUpFloatingWindowEnabled) {
        store.floatingAgent.requestToggleHeadsUpPopover();
      }
    },
    gatedOptions
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.see_keyboard_shortcuts,
    store.interface.openKeyboardShortcutsModal,
    gatedOptions
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.go_back,
    () => {
      store.navigation.goBack();
    },
    gatedOptionsWithOverride
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.go_forward,
    () => {
      store.navigation.goForward();
    },
    gatedOptionsWithOverride
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.go_home,
    () => {
      store.navigation.goToNotes();
    },
    gatedOptions
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.go_collections,
    () => {
      store.navigation.goToCollections();
    },
    gatedOptions
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.go_settings,
    () => {
      store.settingsModal.open();
    },
    gatedOptions
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.close_tab,
    () => {
      const activeTab = store.navigation.tabs.tabsMap.get(store.navigation.tabs.activeTabId);
      if (!activeTab) {
        return;
      }

      if (closeMacDesktopWindowWhenOnlyOneTabRemains()) {
        return;
      }

      if (!activeTab.isDefault) {
        store.navigation.tabs.closeTab({ tabId: store.navigation.tabs.activeTabId });
        return;
      }

      const activePaneTabs = store.navigation.tabs.getTabsForPane(activeTab.paneId);
      const isBareHomePane = activePaneTabs.length === 1;
      const hasMultipleVisiblePanes = visiblePaneTabs.length >= 2;

      if (isBareHomePane && hasMultipleVisiblePanes) {
        store.navigation.tabs.closePane({ paneId: activeTab.paneId });
        return;
      }
    },
    gatedOptionsWithOverride
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_1,
    () => {
      goToRelativeTab(1);
    },
    gatedOptions
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_2,
    () => {
      goToRelativeTab(2);
    },
    gatedOptions
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_3,
    () => {
      goToRelativeTab(3);
    },
    gatedOptions
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_4,
    () => {
      goToRelativeTab(4);
    },
    gatedOptions
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_5,
    () => {
      goToRelativeTab(5);
    },
    gatedOptions
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_6,
    () => {
      goToRelativeTab(6);
    },
    gatedOptions
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_7,
    () => {
      goToRelativeTab(7);
    },
    gatedOptions
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_8,
    () => {
      goToRelativeTab(8);
    },
    gatedOptions
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.go_to_tab_9,
    () => {
      goToRelativeTab(9);
    },
    gatedOptions
  );

  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_1",
    index: 1,
    openRelativePinnedItem,
    options: gatedOptions,
  });
  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_2",
    index: 2,
    openRelativePinnedItem,
    options: gatedOptions,
  });
  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_3",
    index: 3,
    openRelativePinnedItem,
    options: gatedOptions,
  });
  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_4",
    index: 4,
    openRelativePinnedItem,
    options: gatedOptions,
  });
  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_5",
    index: 5,
    openRelativePinnedItem,
    options: gatedOptions,
  });
  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_6",
    index: 6,
    openRelativePinnedItem,
    options: gatedOptions,
  });
  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_7",
    index: 7,
    openRelativePinnedItem,
    options: gatedOptions,
  });
  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_8",
    index: 8,
    openRelativePinnedItem,
    options: gatedOptions,
  });
  useRelativePinnedItemHotkey({
    action: "go_to_pinned_item_9",
    index: 9,
    openRelativePinnedItem,
    options: gatedOptions,
  });

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.move_notes_to_trash,
    async () => {
      if (await trashSelectedNotes()) {
        return;
      }

      await moveActiveNoteToTrashAndCloseTab();
    },
    optionsEnabledForSelectedNotesOrNoteViewWithOverride
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.escape,
    () => {
      const listState = resolveChatSelectionAttachListState({
        pageStore,
        activeTab,
        activeTabPath: activeTab?.path ?? store.navigation.tabs.NEW_TAB_PATH,
      });

      if (listState?.isSelectModeActive) {
        listState.reset();
        return;
      }

      if (listState?.keyboardHighlightedItemId) {
        listState.highlightItem();
        return;
      }

      if (tabNotePageStore) {
        executeEscapeShortcut({ store, notesViewPageStore: tabNotePageStore });
      } else {
        store.navigation.tabs.openInNewTab({ path: appRoutes.notesList({}).path });
      }
    },
    gatedOptions
  );

  useResolvedHotkey(KEYBOARD_SHORTCUT_BINDINGS.next_pane, switchToNextPaneAndFocus, {
    ...gatedOptions,
    preventDefault: true,
  });

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.toggle_sidebar,
    () => {
      if (!canToggleSidebar) return;
      store.interface.toggleSidebar();
    },
    gatedOptionsWithOverride
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.focus_mode,
    toggleFocusMode,
    gatedOptionsWithOverride
  );

  // Editor Keyboard shortcuts
  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.editor_find),
    () => tabNotePageStore?.findInNote?.open(),
    {
      ...optionsEnabledOnNoteViewTabOutsideVersionHistory,
      ignoreInputs: false,
      preventDefault: true,
    }
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.editor_find_next),
    () => tabNotePageStore?.findInNote?.findNext(),
    optionsEnabledWithFindInNoteOpen
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.organize_selected_notes,
    () => {
      if (openOrganizeForSelectedNotes()) {
        return;
      }

      if (!activeNoteId) return;

      tabNotePageStore?.requestOpenOrganizeMenu();
    },
    optionsEnabledForSelectedOrganizableNotesOrNoteView
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.auto_organize_selected_notes,
    async () => {
      if (await autoOrganizeSelectedNotes()) {
        return;
      }

      if (!store.floatingAgent.isMemAgentAccessEnabled || !activeNoteId) return;

      await actions.autoOrganizeNotes({
        noteIds: [activeNoteId],
        store,
      });
    },
    optionsEnabledForSelectedNotesOrNoteViewWithOverride
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.find_in_note_previous_result_alternate),
    () => {
      tabNotePageStore?.findInNote?.findPrevious();
    },
    optionsEnabledWithFindInNoteOpen
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.open_note_in_split_view,
    () => {
      if (!activeNoteId) return;

      store.navigation.goToNote({
        noteId: activeNoteId,
        activeTabId: activeTab?.id,
        openInInactivePane: true,
      });
    },
    optionsEnabledOnNoteViewTab
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.move_note_to_other_split_view,
    () => {
      if (!activeNoteId || !activeTab) return;

      store.navigation.goToNote({
        noteId: activeNoteId,
        activeTabId: activeTab.id,
        openInInactivePane: true,
      });
      store.navigation.tabs.closeTab({ tabId: activeTab.id });
    },
    optionsEnabledOnNoteViewTab
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.cleanup_note,
    async () => {
      const note = tabNotePageStore?.itemObservable;
      if (!isCleanableNote(note)) return;

      await note.cleanup();
    },
    optionsEnabledOnNoteViewTab
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.toggle_voice_cleanup_panel,
    () => {
      tabNotePageStore?.requestToggleVoiceCleanupPanel();
    },
    optionsEnabledOnNoteViewTabWithPreventDefault
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.accept_voice_cleanup_changes,
    () => {
      tabNotePageStore?.requestAcceptVoiceCleanupChanges();
    },
    optionsEnabledOnNoteViewTabWithPreventDefault
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.cleanup_and_auto_organize_note,
    async () => {
      const note = tabNotePageStore?.itemObservable;
      if (!store.floatingAgent.isMemAgentAccessEnabled || !activeNoteId || !isCleanableNote(note)) {
        return;
      }

      const cleanupResult = await note.cleanup();
      if (!cleanupResult.success) return;

      await actions.autoOrganizeNotes({
        noteIds: [activeNoteId],
        store,
      });
    },
    optionsEnabledOnNoteViewTab
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.copy_note_link,
    async () => {
      if (!activeNoteId) return;

      await actions.copyNoteLinkToClipboard({ noteId: activeNoteId });
    },
    optionsEnabledOnNoteViewTabWithPreventDefault
  );

  useResolvedHotkey(
    KEYBOARD_SHORTCUT_BINDINGS.pin_note,
    () => {
      if (tabNotePageStore?.itemObservable) {
        void tabNotePageStore.itemObservable.toggleFavorite();
        return;
      }

      const activeCollectionsViewPageStore = getActiveCollectionsViewPageStore();
      if (activeCollectionsViewPageStore?.collectionObservable) {
        void activeCollectionsViewPageStore.collectionObservable.toggleFavorite();
        return;
      }

      if (!canPinHighlightedNotesListItem || !highlightedNotesListItemId) {
        return;
      }

      void store.notes
        .getNoteObservableById({ noteId: highlightedNotesListItemId })
        ?.toggleFavorite();
    },
    optionsEnabledForPinNote
  );
};
